/** @jsxImportSource @emotion/react */
import React from 'react';
import { DatePicker, Form, Input, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import commonStyles from '../../../../../styles/commonStyles';
import { datePickerFormat } from '../../../../../i18n';
import { formStyles } from '../constants';
import type { LockedLocationInfo } from '../types';

interface LockedLocationSectionProps {
  info: LockedLocationInfo;
}

/**
 * Read-only location display for the "bed page" scenario
 * where location is pre-determined and cannot be changed.
 */
const LockedLocationSection: React.FC<LockedLocationSectionProps> = ({ info }) => {
  const { t } = useTranslation('plantings');
  return (
  <>
    <Typography.Title level={5} css={formStyles.sectionTitle}>
      {t('form.location.placement')}
    </Typography.Title>

    <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
      <Typography.Text>{t('form.location.fieldValue', { value: info.fieldLabel })}</Typography.Text>
      <Typography.Text>{t('form.location.plotValue', { value: info.plotLabel })}</Typography.Text>
      <Typography.Text>{t('form.location.bedValue', { value: info.bedLabel })}</Typography.Text>
      <Typography.Text type="secondary">
        {t('form.location.lockedHint')}
      </Typography.Text>

      {/* Hidden fields keep location values in form state */}
      <Form.Item name={['locations', 0, 'id']} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={['locations', 0, 'fieldId']} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={['locations', 0, 'plotId']} hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={['locations', 0, 'bedId']} hidden>
        <Input type="hidden" />
      </Form.Item>

      <Space direction="horizontal" css={formStyles.fullWidthSpace}>
        <Form.Item
          label={t('details.startDate')}
          name={['locations', 0, 'fromDate']}
          rules={[{ required: true, message: t('form.location.startDateRequired') }]}
          css={formStyles.flexOne}
        >
          <DatePicker format={datePickerFormat()} css={commonStyles.fullWidth} />
        </Form.Item>
        <Form.Item
          label={t('form.location.endDate')}
          name={['locations', 0, 'toDate']}
          css={formStyles.flexOne}
        >
          <DatePicker format={datePickerFormat()} css={commonStyles.fullWidth} allowClear />
        </Form.Item>
      </Space>
    </Space>
  </>
  );
};

export default LockedLocationSection;
